import React, { useState } from 'react';
import { Box, Dialog, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ctaButtonSx, secondaryButtonSx } from '../../../../../components/ui/Buttons/buttonStyles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

type ImproveVariant = 'preview' | 'module' | 'program' | 'lesson' | 'section';

interface ImproveWithAIModalProps {
  variant: ImproveVariant;
  open: boolean;
  onClose: () => void;
  /**
   * Return (or resolve) `false` when the request did not go through: the modal
   * then keeps what the admin typed so they can retry without re-typing it.
   * Anything else (including nothing) clears the fields, as before.
   */
  onSubmit: (feedback: string, extra?: { newTitle?: string; promptNotes?: string; confirmed?: boolean }) => unknown;
  isLoading?: boolean;
  /** Button label while `isLoading` (e.g. "Saving..."). */
  loadingText?: string;
  /** Module/lesson name displayed in the title when variant is module/lesson */
  entityName?: string;
  /** Overrides the "Module"/"Lesson" prefix on the entityName line (e.g. "Case Study"). */
  entityLabel?: string;
  /**
   * Reassures the admin that an AI-generated video already in the lesson
   * survives the regenerate (see utils/lessonMedia.preserveVideos).
   */
  showVideoNotice?: boolean;
  /**
   * Caller-supplied warning shown above the actions in the same style as the
   * module/lesson banner — e.g. "this replaces your existing quiz questions".
   */
  notice?: string;
  /** Overrides the module/lesson banner text (e.g. when the lesson is drafted, not rewritten). */
  bannerText?: string;
}

const VARIANT_CONFIG: Record<ImproveVariant, { title: string; feedbackLabel: string; feedbackPlaceholder: string }> = {
  preview: {
    title: 'Improve with Elliott',
    feedbackLabel: 'Describe your changes to the program:',
    feedbackPlaceholder: 'e.g. Make it shorter and focus on leadership skills'
  },
  module: {
    title: 'Improve with Elliott',
    feedbackLabel: 'Prompt notes for regeneration:',
    feedbackPlaceholder: 'What should Elliott change in this module?'
  },
  program: {
    title: 'Improve program structure',
    feedbackLabel: 'Describe your desired changes',
    feedbackPlaceholder: 'Type in your modifications here'
  },
  lesson: {
    title: 'Improve with Elliott',
    feedbackLabel: 'Prompt notes for regeneration:',
    feedbackPlaceholder: 'What should this lesson cover, or what should Elliott change?'
  },
  // Section-scoped improve (Case Studies / Live Simulation / Quiz) — no "regenerate all lessons" banner.
  section: {
    title: 'Improve with Elliott',
    feedbackLabel: 'Describe how you’d like to improve this:',
    feedbackPlaceholder: 'e.g. Make the scenarios more challenging and industry-specific'
  }
};

const ImproveWithAIModal: React.FC<ImproveWithAIModalProps> = ({
  variant,
  open,
  onClose,
  onSubmit,
  isLoading = false,
  loadingText = 'Improving...',
  entityName,
  entityLabel,
  showVideoNotice = false,
  notice,
  bannerText
}) => {
  const [feedback, setFeedback] = useState('');
  const [newTitle, setNewTitle] = useState('');

  const config = VARIANT_CONFIG[variant];
  // Section-scoped improves accept an empty prompt — that's a plain regenerate
  // (e.g. "Regenerate simulation" replaces the scenario and rubric).
  const canSubmit = (variant === 'section' || feedback.trim().length >= 5) && !isLoading;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const ok = await onSubmit(feedback, {
      newTitle: newTitle || undefined
    });
    if (ok === false) return;
    setFeedback('');
    setNewTitle('');
  };

  const handleClose = () => {
    if (isLoading) return;
    setFeedback('');
    setNewTitle('');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: '#1A1A1A',
          borderRadius: '12px',
          border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
          p: 3
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 600, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)' }}>
            {config.title}
          </Typography>
        </Box>
        <Box component="button" onClick={handleClose} sx={{ background: 'none', border: 'none', cursor: 'pointer', p: 0.5 }}>
          <CloseIcon sx={{ color: 'var(--Text-text-secondary, #B6B6B6)', fontSize: 20 }} />
        </Box>
      </Box>

      {entityName && (
        <Typography sx={{ fontSize: 13, fontFamily: 'Inter', color: 'var(--Text-text-tertiary, #868686)', mb: 2 }}>
          {entityLabel ?? (variant === 'module' ? 'Module' : 'Lesson')}: {entityName}
        </Typography>
      )}

      {/* New Title (module & lesson variants) */}
      {(variant === 'module' || variant === 'lesson') && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 13, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', mb: 0.5 }}>
            New title desired
          </Typography>
          <Box
            component="input"
            value={newTitle}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewTitle(e.target.value)}
            placeholder="Leave empty to keep current title"
            sx={{
              width: '100%', background: '#0F0F0F',
              border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
              borderRadius: '8px', p: 1.5, color: 'var(--Text-text-primary, #FDFDFD)',
              fontFamily: 'Inter', fontSize: 14, outline: 'none',
              '&:focus': { borderColor: 'var(--Stroke-highlight, #F79420)' }
            }}
          />
        </Box>
      )}

      {/* Feedback / prompt notes */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 13, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', mb: 0.5 }}>
          {config.feedbackLabel}
        </Typography>
        <Box
          component="textarea"
          value={feedback}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFeedback(e.target.value)}
          placeholder={config.feedbackPlaceholder}
          sx={{
            width: '100%', minHeight: 120, background: '#0F0F0F',
            border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
            borderRadius: '8px', p: 2, color: 'var(--Text-text-primary, #FDFDFD)',
            fontFamily: 'Inter', fontSize: 14, lineHeight: '22px', resize: 'vertical', outline: 'none',
            '&:focus': { borderColor: 'var(--Stroke-highlight, #F79420)' }
          }}
        />
      </Box>

      {/* Warning banner (module & lesson) */}
      {(variant === 'module' || variant === 'lesson') && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.25,
          p: 1.25, mb: 2, borderRadius: '5px',
          background: 'rgba(247, 148, 32, 0.1)'
        }}>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: 'var(--Stroke-highlight, #F79420)', flexShrink: 0 }} />
          <Typography sx={{ fontSize: 12, fontWeight: 500, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)', lineHeight: '16px' }}>
            {bannerText ??
              (variant === 'module'
                ? 'All lessons within this module will be regenerated based on your feedback.'
                : 'Elliott rewrites this lesson from your notes, then rebuilds its case studies, Roleplay and quiz to match.')}
          </Typography>
        </Box>
      )}

      {/* Video kept notice — the lesson already has an AI-generated video */}
      {showVideoNotice && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.25,
          p: 1.25, mb: 2, borderRadius: '5px',
          background: 'rgba(247, 148, 32, 0.1)'
        }}>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: 'var(--Stroke-highlight, #F79420)', flexShrink: 0 }} />
          <Typography sx={{ fontSize: 12, fontWeight: 500, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)', lineHeight: '16px' }}>
            Your video will remain in this lesson. You can remove or improve it separately if you like.
          </Typography>
        </Box>
      )}

      {/* Caller-supplied warning — what this regenerate will replace */}
      {notice && (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.25,
          p: 1.25, mb: 2, borderRadius: '5px',
          background: 'rgba(247, 148, 32, 0.1)'
        }}>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: 'var(--Stroke-highlight, #F79420)', flexShrink: 0 }} />
          <Typography sx={{ fontSize: 12, fontWeight: 500, fontFamily: 'Inter', color: 'var(--Text-text-primary, #FDFDFD)', lineHeight: '16px' }}>
            {notice}
          </Typography>
        </Box>
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Box
          component="button"
          onClick={handleClose}
          sx={{ ...secondaryButtonSx, cursor: 'pointer' }}
        >
          Cancel
        </Box>
        <Box
          component="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          sx={{ ...ctaButtonSx, border: 'none', cursor: canSubmit ? 'pointer' : 'not-allowed' }}
        >
          {isLoading ? loadingText : 'Improve with Elliott'}
        </Box>
      </Box>
    </Dialog>
  );
};

export default ImproveWithAIModal;
